/**
 * M3C-04 — the FRONTEND's module registry (DI-D62 c2 / c4: two registries,
 * both code — node definitions in the backend, modules in the frontend; the
 * node contract carries the module key).
 *
 * The FE knows node KEYS for one purpose only: to mount the module registered
 * for a key at its hook (lock `no-fe-registry-of-keys`). Labels, presets,
 * fields and which workflow is the default all come from the server
 * (`workflows/v1`). This file is the ONLY place a node key is spelled
 * (grep test `moduleRegistry.grep.test.ts`).
 *
 * Exactly the nodes this release has a module for (workflows/v1 1.0.0
 * registry; DI-5-1 adds the write-back, DI-5-2 the SkySpecs export — client
 * nodes) plus the generic safe panel (`UnknownNodePanel`): a key this
 * release does not know is never a crash and never hides the set (lock
 * `safe-panel`). `moduleFor` never throws.
 *
 * M3C-09 (workflows/v1 1.4.0, guide §4.14): the server serves `ui.module` =
 * the node INSTANCE's module when the version sets one, else the definition's.
 * The app mounts by what is served (lock `instance-module-wins`). Resolution
 * order, per node:
 *   1. the SERVED `ui.module`, when it is a `ModuleId` this release knows;
 *   2. else the key registry below (as before 1.4.0 — an older server, a
 *      `null` module, or a module key this release does not know);
 *   3. else the safe panel.
 */
import type { WorkflowNode, WorkflowVersion } from "./api/workflows.js";

/** The modules this release can mount. */
export type ModuleId =
  | "projects.wizard"
  | "intake.dropAndDecide"
  | "intake.verdictBand"
  /** DI-5-1 — a CLIENT node run after the approve, never a screen (`runner/clientNodes.ts`). */
  | "intake.writeBackExif"
  /** DI-5-2 — a CLIENT node run after the approve (`skyspecsExport/`). */
  | "intake.exportSkyspecsJson"
  /** M6-NODE-SORT-01 — a CLIENT node run after the approve, after the EXIF update (`sortIntoFolders/`). */
  | "intake.sortIntoFolders";

const MODULE_IDS: readonly ModuleId[] = [
  "projects.wizard",
  "intake.dropAndDecide",
  "intake.verdictBand",
  "intake.writeBackExif",
  "intake.exportSkyspecsJson",
  "intake.sortIntoFolders",
];

/** Is `v` a module this release can mount? (Never true for a prototype key.) */
export function isModuleId(v: unknown): v is ModuleId {
  return typeof v === "string" && (MODULE_IDS as readonly string[]).includes(v);
}

export interface ModuleEntry {
  module: ModuleId;
  /**
   * The node's parameter shape this module understands. A server that sends
   * a newer shape still mounts the module (params are read defensively);
   * the number is here so a later bump is a recorded, testable change.
   */
  supportedParamsVersion: 1;
}

const REGISTRY: Readonly<Record<string, ModuleEntry>> = {
  "node.project_create": {
    module: "projects.wizard",
    supportedParamsVersion: 1,
  },
  "node.data_injection": {
    module: "intake.dropAndDecide",
    supportedParamsVersion: 1,
  },
  "node.qc_coverage": {
    module: "intake.verdictBand",
    supportedParamsVersion: 1,
  },
  // DI-5-1 — the write-back of SubjectDistance + the aerios XMP tags into the
  // set's images on the pilot's PC, run after the approve (hook da.approve)
  "node.write_back_exif": {
    module: "intake.writeBackExif",
    supportedParamsVersion: 1,
  },
  // DI-5-2 — the SkySpecs export (the four-field {turbine}.json + the
  // <serial>/<side>/ tree, images copied) into a folder on the pilot's PC,
  // run after the approve, after the write-back when the version has both;
  // fieldSetVersion 1 is provisional (DI-D5 c3)
  "node.export_skyspecs_json": {
    module: "intake.exportSkyspecsJson",
    supportedParamsVersion: 1,
  },
  // M6-NODE-SORT-01 — the images sorted into a Flight → Blade → Side folder
  // tree on the pilot's PC, run after the approve and after the EXIF update
  // (the server serves the client nodes in run order, `rankInHook`)
  "node.sort_into_folders": {
    module: "intake.sortIntoFolders",
    supportedParamsVersion: 1,
  },
};

/** The node key whose module is the verdict band (the verify card's mount). */
export const VERDICT_BAND_NODE = "node.qc_coverage";

export type ModuleResolution =
  | {
      kind: "module";
      key: string;
      module: ModuleId;
      entry: ModuleEntry;
      /** `served` = the server's `ui.module` decided; `key` = the key registry. */
      via: "served" | "key";
    }
  | { kind: "unknown"; key: string; module: "unknown" };

/**
 * node key (+ the module the server SERVES for it, workflows/v1 1.4.0) → the
 * module to mount: the served module when this release knows it, else the
 * module registered for the key, else the safe panel. Never throws.
 */
export function moduleFor(
  key: string | null | undefined,
  servedModule?: string | null,
): ModuleResolution {
  const k = typeof key === "string" ? key : "";
  if (isModuleId(servedModule))
    return {
      kind: "module",
      key: k,
      module: servedModule,
      entry: { module: servedModule, supportedParamsVersion: 1 },
      via: "served",
    };
  const entry = Object.prototype.hasOwnProperty.call(REGISTRY, k)
    ? REGISTRY[k]
    : undefined;
  if (!entry) return { kind: "unknown", key: k, module: "unknown" };
  return { kind: "module", key: k, module: entry.module, entry, via: "key" };
}

/** A served node as far as mounting goes: its key and (1.4.0) its `ui.module`. */
export type MountableNode = Pick<WorkflowNode, "key"> & {
  ui?: { module?: string | null } | null;
};

/** The module a SERVED node mounts — `moduleFor(node.key, node.ui.module)`. */
export function moduleOfNode(node: MountableNode): ModuleResolution {
  return moduleFor(node.key, node.ui?.module ?? null);
}

/**
 * The node of a version this release mounts as `module` (by its served
 * `ui.module`, else its key — see `moduleOfNode`), or null.
 * (The FE asks "which node of the anchor's version sits where my module is",
 * never "what comes next" — DI-D57.) Disabled nodes do not mount.
 */
export function nodeForModule(
  version: Pick<WorkflowVersion, "nodes"> | null | undefined,
  module: ModuleId,
): WorkflowNode | null {
  for (const n of version?.nodes ?? []) {
    if (!n.enabled) continue;
    const r = moduleOfNode(n);
    if (r.kind === "module" && r.module === module) return n;
  }
  return null;
}

/**
 * The nodes of a version this release cannot mount (`moduleOfNode` → unknown:
 * neither the served module nor the key is known):
 * the screens that mount a version show the safe panel for each.
 */
export function unknownNodes(
  version: Pick<WorkflowVersion, "nodes"> | null | undefined,
): WorkflowNode[] {
  return (version?.nodes ?? []).filter(
    (n) => n.enabled && moduleOfNode(n).kind === "unknown",
  );
}
